// src/forms/data_hub.js
import defaultsDeep from 'lodash.defaultsdeep';
import { v4 as uuidv4 } from 'uuid';

import {
    return_valid_or_default_forms_domain,
    inject_styles,
    is_plain_object
} from '../helpers/index';
import {
    default_forms_domain,
    default_data_hub_css_url,
    default_organization_id,
    default_theme,
    build_organization_metadata
} from '../helpers/static_vars';
import { normalize_data_hub_organization_id_string } from '../helpers/action_request_helpers';
import { normalize_data_hub_privacy_policy_string } from '../helpers/data_hub_privacy_policy';
import { get_most_parent_url_params } from '../helpers/parse_url_params';
import { init_actblue } from '../helpers/actblue_integration';
import { init_analytics_bootstrap } from '../analytics/bootstrap';
import { addDataHubMessageListener } from './cors_iframe';
import { register_user_data_cache_sync_listener } from '../helpers/user_data_cache_sync';
import { show_error_popover } from './html_element_helpers/error_popover';
import { log_console_error_and_track_to_action_endpoint_with_rate_limit } from './html_element_helpers/error_tracking';
import { inject_data_hub_css_link, run_theme_json_to_base64 } from './html_element_helpers/form_styling';

// Standalone helpers that take `data_hub_instance` as their first argument. Each lives in
// its own file so this class stays focused on field initialization. They are aliased
// because the class exposes identically-named methods that delegate to them.
import {
    return_organization_metadata,
    set_page_metadata_defaults as set_page_metadata_defaults_helper
} from './data_hub_metadata';
import { got_cors_user_data_cache as got_cors_user_data_cache_helper } from './data_hub_cors_user_data';
import {
    forget_user as forget_user_helper,
    got_clear_jwt_response as got_clear_jwt_response_helper
} from './data_hub_forget_user';
import {
    setup_user_data_loaded_promise,
    start_data_hub_init as start_data_hub_init_helper
} from './data_hub_init';
// Web push disabled for now — not shipping while focused on /analytics web testing.
// Re-enable this import together with the web_push_* class methods below.
// import {
//     get_web_push_status as get_web_push_status_helper,
//     initialize_data_hub_web_push,
//     web_push_opt_out as web_push_opt_out_helper,
//     web_push_optin_request as web_push_optin_request_helper
// } from '../web_push/data_hub_web_push';

export class data_hub_class {
    constructor(config = {}, callback_function_after_init = () => { }) {
        // Multi-instance is unsupported. The analytics pushState/replaceState wrappers bind
        // to whichever instance constructs first, <data-hub-signup-form> reads window.data_hub
        // so all forms feed one instance, and identity / user_data fetches run twice. Warn
        // instead of throwing so existing call sites that race the form custom element keep
        // working — the second construction will mostly mirror the first, but with subtle
        // ordering bugs around analytics and ActBlue refcodes.
        if (typeof window !== 'undefined' && window.data_hub && window.data_hub.is_initialized) {
            console.warn(
                'data_hub_class: a DataHub instance already exists on window.data_hub. '
                + 'Multi-instance is not supported — auto-event listeners and SPA history '
                + 'wrappers bind to the first instance only. Reuse window.data_hub or call '
                + 'window.data_hub.forget_user() instead of constructing a second instance.'
            );
        }

        const default_error_function = (internal_error_message, external_error_message) => {
            log_console_error_and_track_to_action_endpoint_with_rate_limit({
                internal_error_message,
                external_error_message,
                data_hub_instance: this,
                user_data: this.user_data || {},
                metadata: this.page_metadata || {},
                maximum_error_tracking_requests_per_page: 3
            });
            show_error_popover(external_error_message, this.target || document);
        };
        this.error_function = config.error_function || default_error_function;

        addDataHubMessageListener(this);

        // SAVE-direction cache sync: persists user_data to parent LS + mirrors it to the
        // action-origin LS on every data-hub-new-user-data event (idempotent, registered once).
        register_user_data_cache_sync_listener();

        // Errors are handled inside init_actblue via error_function; promise resolves after catch.
        this.has_init_actblue_merged_configuration = false;
        init_actblue(this.error_function);

        // organization_metadata cascade: explicit config > window.data_hub_organization_metadata
        // (injected by the landing page) > build_organization_metadata (baked-in blob) > {}.
        this.organization_metadata = return_organization_metadata(
            config,
            typeof window !== 'undefined' ? window : null,
            build_organization_metadata
        );

        // Resolution chain: explicit config > organization_metadata > build default.
        // normalize_data_hub_organization_id_string returns null for empty / non-UUID values.
        this.organization_id =
            normalize_data_hub_organization_id_string(config?.organization_id)
            || normalize_data_hub_organization_id_string(this.organization_metadata?.organization_id)
            || normalize_data_hub_organization_id_string(default_organization_id)
            || null;

        const forms_domain_from_config = config.data_hub_action_domain || config.forms_domain
            || this.organization_metadata.data_hub_action_domain || this.organization_metadata.forms_domain;
        this.forms_domain = return_valid_or_default_forms_domain(forms_domain_from_config || default_forms_domain);
        this.url_params = get_most_parent_url_params(this.error_function);
        this.iterableCampaignId = null;
        this.iterableTemplateId = null;

        this.prefilled_forms = new Set();
        this.data_hub_init_id = uuidv4();

        this.user_data = is_plain_object(config.user_data) ? config.user_data : {};
        // Highest-priority explicit seed (from `data-hub-initial-user-data` or constructor
        // config.user_data). Retained separately so the cache cascade can re-apply it on top
        // after async server/iframe data lands — see helpers/user_data_cache_sync.js. URL
        // params are recomputed from the already-cached `this.url_params` when needed, so no
        // separate parsed-url field is stored here.
        this.config_user_data = is_plain_object(config.user_data) ? { ...config.user_data } : {};
        // Last user_data signature mirrored to the action-origin CORS cache (dedup guard).
        this._last_cors_user_data_signature = null;
        this.jwt_cookie = null;

        this.privacy_policy = normalize_data_hub_privacy_policy_string(config.privacy_policy);

        this.actblue_token = config.actblue_token || null;
        this.actblue_amounts = config.actblue_amounts || [5, 10, 20, 50, 100];
        this.actblue_default_page_url = config.actblue_default_page_url || null;
        this.actblue_preview = config.actblue_preview || false;
        this.actblue_css_url = config.actblue_css_url || 'https://qtera.org/css/actblue_modal_600_max.css';

        this.bind_tel_inputs_intl = config.bind_tel_inputs_intl || false;

        // Builds user_data_loaded_promise + assigns resolve_user_data_loaded / reject_user_data_loaded.
        // The chained .then handler merges window.data_hub_user_data, dispatches
        // `data-hub-new-user-data`, and binds tel inputs when enabled.
        setup_user_data_loaded_promise(this);

        // Page metadata + Iterable IDs (refcodes, campaign / template).
        set_page_metadata_defaults_helper(this, config);

        this.target = config.target || null;
        this.style_target = config.style_target || null;
        this.data_hub_css_url = config.data_hub_css_url || default_data_hub_css_url || null;
        this.prefill_input_selector = '[data-allow-data-hub-prefill="true"]';
        this.reset_button_selector = '.data-hub-reset-form';
        this.prefill_all_forms = config.prefill_all_forms ?? true;
        this.block_page_on_user_data_load = config.block_page_on_user_data_load ?? true;
        this.prefill_timeout = config.prefill_timeout || 10000;
        this.has_hit_prefill_timeout = false;
        this.get_iterable_user_data_via_hmac_key = config.get_iterable_user_data_via_hmac_key ?? true;
        this.callback_function_after_init = callback_function_after_init;
        this.web_push_settings = is_plain_object(config.web_push_settings)
            ? config.web_push_settings
            : {};
        this.web_push_state = {};

        this.is_data_hub_form_submission_in_progress = false;
        this.is_initialized = false;

        init_analytics_bootstrap(this, config);

        // Theme resolution: explicit config.theme > organization_metadata.theme_json > build default.
        // organization_metadata is merged synchronously above (config + window.data_hub_organization_metadata)
        // so server-injected metadata themes apply on first paint with no flash. If organization_metadata
        // is fetched asynchronously and its theme_json arrives later, start_data_hub_init re-injects —
        // config_theme is preserved so explicit caller-passed colors keep winning over the async theme_json.
        this.config_theme = is_plain_object(config.theme) ? config.theme : {};
        this.theme = defaultsDeep(
            {},
            this.config_theme,
            this.organization_metadata?.theme_json || {},
            default_theme
        );
        if (this.data_hub_css_url && config.inject_data_hub_css !== false) {
            inject_data_hub_css_link(
                this.style_target || (typeof document !== 'undefined' ? document.head : null),
                {
                    data_hub_css_url: this.data_hub_css_url,
                    error_function: this.error_function
                }
            );
        }
        if (this.style_target) {
            inject_styles(this.theme, this.style_target);
        }

        // Begin initialization
        this.start_data_hub_init();
    }

    callDataHubDispatchEventThenCallbackInit() {
        this.is_initialized = true;

        // Ensure the instance is accessible globally so late consumers can find it
        if (typeof window !== 'undefined' && !window.data_hub) {
            window.data_hub = this;
        }

        document.dispatchEvent(new CustomEvent('data-hub-init', {
            detail: { data_hub_instance: this },
            bubbles: true,
            composed: true
        }));
        this.callback_function_after_init(this);
        this.load_full_analytics_after_init();
        // Disabled while focusing on /analytics web testing. Re-enable for web push QA.
        // initialize_data_hub_web_push(this);
    }

    load_full_analytics_after_init() {
        // web_settings is set synchronously by init_analytics_bootstrap during the
        // constructor. It is mutated in place once full analytics finishes loading.
        if (!this.web_settings?.enabled) {
            return null;
        }
        if (this.analytics_init_promise) {
            return this.analytics_init_promise;
        }
        this.analytics_init_promise = import(
            /* webpackChunkName: "data_hub_web_analytics" */
            '../analytics/index'
        ).then((analytics_module) => {
            if (typeof analytics_module?.init_analytics === 'function') {
                return analytics_module.init_analytics(this);
            }
            return null;
        }).catch((error) => {
            this.error_function?.('Error loading Data Hub web analytics: ' + error, null);
            return null;
        });
        return this.analytics_init_promise;
    }

    static theme_json_to_base64(theme) {
        return run_theme_json_to_base64(theme);
    }

    // Instance-method wrappers around the standalone helpers above. External callers
    // (cors_iframe, data_hub_forms, shared_form_ux_functions, web push triggers) use
    // `data_hub_instance.foo(...)`; the real implementation lives in the imported module.
    // `@see` lets IDE jump-to-definition land on the source file.

    /** @see {@link start_data_hub_init_helper} */
    start_data_hub_init() {
        return start_data_hub_init_helper(this);
    }

    /** @see {@link got_cors_user_data_cache_helper} */
    got_cors_user_data_cache(payload) {
        return got_cors_user_data_cache_helper(this, payload);
    }

    /** @see {@link got_clear_jwt_response_helper} */
    got_clear_jwt_response(payload) {
        return got_clear_jwt_response_helper(this, payload);
    }

    /** @see {@link forget_user_helper} */
    forget_user(options) {
        return forget_user_helper(this, options);
    }

    // Web push disabled for now — see the commented import at the top of this file.
    // Methods are kept as safe no-ops so any existing caller doesn't hit a ReferenceError;
    // re-enable by restoring the import and the helper calls below.
    web_push_optin_request(options) {
        // return web_push_optin_request_helper(this, options);
        return null;
    }

    web_push_opt_out(options) {
        // return web_push_opt_out_helper(this, options);
        return null;
    }

    web_push_get_status(options) {
        // return get_web_push_status_helper(this, options);
        return null;
    }
}
